import { defineComponent, ref, computed, inject, watch, watchEffect } from 'vue';
import { useLocalizationSupport } from '../../composables/localization-support/index.js';
import { useStoreModule } from '../../composables/store-module/index.js';
import HoistComponent from '../hoist-component/HoistComponent.vue.js';
import UiRenderNothing from '../ui-render-nothing/UiRenderNothing.vue.js';
import { getValidationErrors } from '../../helpers/validation.js';
import { generateElementId } from '../../helpers/generators.js';
import { factsheetHeaderFactory, defaultFactsheetHeaderConfiguration } from '../factsheet-header/setup/index.js';

var _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "AlertTriggerControls",
  props: {
    locale: { type: null, required: true },
    securityId: { type: null, required: true }
  },
  emits: ["store-error", "data-load", "cancel", "delete", "save", "exchange-change"],
  setup(__props, { emit: emits }) {
    var _a;
    const props = __props;
    const hasDataLoaded = ref(false);
    const elementId = generateElementId();
    const upperLimit = ref(null);
    const lowerLimit = ref(null);
    const action = ref("FETCH");
    const componentInputs = computed(() => {
      return {
        securityId: props.securityId,
        language: props.locale,
        action: action.value,
        limit: {
          above: upperLimit.value,
          below: lowerLimit.value
        }
      };
    });
    const translator = inject("translator");
    const numberFormatter = inject("numberFormatter");
    const dateFormatter = inject("dateFormatter");
    const injectedStoreModule = inject("storeModule");
    const localizationSupport = useLocalizationSupport(props, {
      translator,
      numberFormatter,
      dateFormatter
    });
    const { translate, textFormat } = localizationSupport;
    const storeModule = useStoreModule(
      {
        storeModule: injectedStoreModule
      },
      componentInputs,
      emits,
      { requestDataOnComponentInputsChange: false }
    );
    const { isLoadingStoreData, storeData, storeMeta, storeRequestData } = storeModule;
    const FactsheetHeader = factsheetHeaderFactory({
      translator,
      numberFormatter,
      dateFormatter,
      storeModule: (injectedStoreModule == null ? void 0 : injectedStoreModule.getDependencies) ? injectedStoreModule.getDependencies().factsheetHeader : void 0,
      options: {
        componentName: "factsheet-header",
        highChartOptions: (_a = defaultFactsheetHeaderConfiguration.options) == null ? void 0 : _a.highChartOptions
      }
    });
    const fetchData = async () => {
      action.value = "FETCH";
      hasDataLoaded.value = false;
      await storeRequestData();
    };
    watch(
      () => props,
      () => {
        void fetchData();
      },
      { deep: true, immediate: true }
    );
    const upperLimitValidationErrorsBag = ref([]);
    const lowerLimitValidationErrorsBag = ref([]);
    const hasValidUpperLimit = computed(() => upperLimitValidationErrorsBag.value.length === 0);
    const hasValidLowerLimit = computed(() => lowerLimitValidationErrorsBag.value.length === 0);
    const hasValidForm = computed(() => hasValidUpperLimit.value && hasValidLowerLimit.value);
    const onCancel = () => {
      emits("cancel");
    };
    const onExchangeChange = (payload) => {
      emits("exchange-change", payload);
    };
    const onDelete = async () => {
      action.value = "DELETE";
      await storeRequestData();
      upperLimit.value = null;
      lowerLimit.value = null;
      emits("delete");
    };
    const onSave = async () => {
      validateForm();
      if (hasValidForm.value) {
        action.value = "SAVE";
        await storeRequestData();
        emits("save");
      }
    };
    const validateForm = () => {
      validateUpperLimit();
      validateLowerLimit();
    };
    const validateUpperLimit = () => {
      var _a2, _b;
      upperLimitValidationErrorsBag.value = [];
      upperLimitValidationErrorsBag.value = upperLimit.value ? getValidationErrors(upperLimit.value, (_a2 = storeMeta.value) == null ? void 0 : _a2.validationRules.upperLimit) : [];
      if (((_b = storeData.value) == null ? void 0 : _b.lastPrice) && upperLimit.value && upperLimit.value <= storeData.value.lastPrice) {
        upperLimitValidationErrorsBag.value.push({ label: "priceMismatch" });
      }
    };
    const validateLowerLimit = () => {
      var _a2, _b;
      lowerLimitValidationErrorsBag.value = [];
      lowerLimitValidationErrorsBag.value = lowerLimit.value ? getValidationErrors(lowerLimit.value, (_a2 = storeMeta.value) == null ? void 0 : _a2.validationRules.lowerLimit) : [];
      if (((_b = storeData.value) == null ? void 0 : _b.lastPrice) && lowerLimit.value && lowerLimit.value >= storeData.value.lastPrice) {
        lowerLimitValidationErrorsBag.value.push({ label: "priceMismatch" });
      }
    };
    watchEffect(() => {
      var _a2, _b;
      if (!hasDataLoaded.value && storeData.value) {
        upperLimit.value = (_a2 = storeData.value.limit.above) != null ? _a2 : null;
        lowerLimit.value = (_b = storeData.value.limit.below) != null ? _b : null;
        hasDataLoaded.value = true;
      }
    });
    return { __sfc: true, props, emits, hasDataLoaded, elementId, upperLimit, lowerLimit, action, componentInputs, translator, numberFormatter, dateFormatter, injectedStoreModule, localizationSupport, translate, textFormat, storeModule, isLoadingStoreData, storeData, storeMeta, storeRequestData, FactsheetHeader, fetchData, upperLimitValidationErrorsBag, lowerLimitValidationErrorsBag, hasValidUpperLimit, hasValidLowerLimit, hasValidForm, onCancel, onExchangeChange, onDelete, onSave, validateForm, validateUpperLimit, validateLowerLimit, HoistComponent, UiRenderNothing };
  }
});

export { _sfc_main as default };
